import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Check, TelegramLogo, Crown } from '@phosphor-icons/react';
import { API_URL } from '../config';

const PERK_COLORS = {
  blue: { text: 'text-[#007AFF]', link: 'text-[#007AFF]/70 hover:text-[#007AFF]' },
  green: { text: 'text-[#34C759]', link: 'text-[#34C759]/70 hover:text-[#34C759]' },
  gold: { text: 'text-[#FF9500]', link: 'text-[#FF9500]/70 hover:text-[#FF9500]' },
};

const PricingPage = () => {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/subscription/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate('/register');
      return;
    }
    if (planId === 'free') return;

    setCheckoutLoading(planId);
    try {
      const response = await axios.post(
        `${API_URL}/api/subscription/checkout`,
        { plan_id: planId, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('Unable to process payment. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const getBillingLabel = (plan) => {
    if (!plan.billing) return '';
    if (plan.billing === 'lifetime') return '';
    return plan.billing === 'yearly' ? '/year' : '/mo';
  };

  const getButtonStyle = (plan) => {
    if (plan.id === 'free') return 'border border-white/20 text-white hover:bg-white/10';
    if (plan.highlighted) return 'bg-[#34C759] text-black hover:bg-[#2db550]';
    if (plan.id === 'lifetime') return 'bg-[#34C759] text-black hover:bg-[#2db550]';
    if (plan.perk_color === 'gold') return 'bg-[#FF9500] text-black hover:bg-[#e08600]';
    return 'bg-[#007AFF] text-white hover:bg-[#005bb5]';
  };

  const isCurrent = (planId) => user && user.subscription_tier === planId;

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase text-white mb-4" data-testid="pricing-title">
            Choose Your Path
          </h1>
          <p className="text-[#8892B0] text-lg max-w-2xl mx-auto">
            Invest in your trading psychology. Choose the plan that fits your needs.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#8892B0]">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pricing-grid">
            {plans.map((plan) => {
              const colors = PERK_COLORS[plan.perk_color] || PERK_COLORS.blue;
              const isHighlighted = plan.highlighted || plan.id === 'lifetime';

              return (
                <div key={plan.id} className="relative" data-testid={`plan-${plan.id}`}>
                  {isHighlighted ? (
                    <div className="tracing-beam p-[2px] h-full">
                      <PlanCard
                        plan={plan}
                        colors={colors}
                        isCurrent={isCurrent(plan.id)}
                        checkoutLoading={checkoutLoading}
                        onSubscribe={handleSubscribe}
                        getBillingLabel={getBillingLabel}
                        getButtonStyle={getButtonStyle}
                      />
                    </div>
                  ) : (
                    <div className="border border-white/10 h-full">
                      <PlanCard
                        plan={plan}
                        colors={colors}
                        isCurrent={isCurrent(plan.id)}
                        checkoutLoading={checkoutLoading}
                        onSubscribe={handleSubscribe}
                        getBillingLabel={getBillingLabel}
                        getButtonStyle={getButtonStyle}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* FAQ */}
        <div className="mt-24">
          <h2 className="font-heading font-bold text-2xl uppercase text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-[#131313] border border-white/5 p-6">
              <h3 className="text-white font-bold mb-2">What's a Human Trade Journal?</h3>
              <p className="text-[#8892B0] text-sm">
                Our coach reviews your trades and provides personalized feedback via Telegram 
                to help improve your trading discipline and psychology.
              </p>
            </div>
            <div className="bg-[#131313] border border-white/5 p-6">
              <h3 className="text-white font-bold mb-2">What's included in Human Coaching?</h3>
              <p className="text-[#8892B0] text-sm">
                1-on-1 sessions with our trading psychology coach via Telegram. 
                Get personalized guidance, accountability, and strategy reviews.
              </p>
            </div>
            <div className="bg-[#131313] border border-white/5 p-6">
              <h3 className="text-white font-bold mb-2">Can I cancel anytime?</h3>
              <p className="text-[#8892B0] text-sm">
                Yes, you can cancel your subscription at any time. You'll continue to have access 
                until the end of your billing period.
              </p>
            </div>
            <div className="bg-[#131313] border border-white/5 p-6">
              <h3 className="text-white font-bold mb-2">What's the Lifetime Membership?</h3>
              <p className="text-[#8892B0] text-sm">
                One payment, forever access. Includes unlimited human coaching sessions, 
                all platform features, and every future update.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PlanCard = ({ plan, colors, isCurrent, checkoutLoading, onSubscribe, getBillingLabel, getButtonStyle }) => (
  <div className="bg-[#131313] p-6 h-full flex flex-col relative">
    {isCurrent && (
      <span className="absolute -top-3 right-4 px-3 py-1 bg-[#34C759] text-black text-xs font-bold uppercase">
        Current Plan
      </span>
    )}
    {plan.highlighted && (
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading font-bold text-lg uppercase text-white">{plan.name}</h3>
        <span className="px-2 py-1 text-xs bg-[#34C759] text-black font-bold uppercase">Best Value</span>
      </div>
    )}
    {!plan.highlighted && (
      <h3 className="font-heading font-bold text-lg uppercase text-white mb-2">{plan.name}</h3>
    )}

    <div className="mb-2">
      <span className="text-4xl font-black text-white">
        ${plan.price >= 1000 ? plan.price.toLocaleString() : plan.price}
      </span>
      {getBillingLabel(plan) && (
        <span className="text-base text-[#8892B0]">{getBillingLabel(plan)}</span>
      )}
    </div>

    {plan.perk && (
      <div className="mb-4">
        <div className={`flex items-center gap-2 font-bold text-sm ${colors.text}`}>
          <TelegramLogo size={16} weight="fill" />
          <span>{plan.perk}</span>
        </div>
        <a
          href={process.env.REACT_APP_TELEGRAM_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-1 text-xs mt-1 ml-6 transition-colors ${colors.link}`}
          data-testid={`${plan.id}-telegram-link`}
        >
          Telegram Community
        </a>
      </div>
    )}
    {plan.id === 'free' && <div className="mb-4" />}

    <ul className="space-y-2 mb-6 flex-1">
      {plan.features.map((feature) => (
        <li key={feature} className="flex items-start gap-2 text-[#8892B0] text-sm">
          <Check size={16} className="text-[#34C759] mt-0.5 flex-shrink-0" />
          {feature}
        </li>
      ))}
    </ul>

    <button
      onClick={() => onSubscribe(plan.id)}
      disabled={checkoutLoading === plan.id || isCurrent}
      className={`w-full py-3 font-bold uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm ${getButtonStyle(plan)}`}
      data-testid={`subscribe-${plan.id}`}
    >
      {checkoutLoading === plan.id ? 'Processing...' :
       isCurrent ? 'Current Plan' :
       plan.id === 'free' ? 'Get Started' : 'Subscribe'}
    </button>
  </div>
);

export default PricingPage;
